/*
 * Lossless openHASP external-theme model.
 *
 * The firmware format is JSONL.  Keeping each parsed object as a record (and
 * editing that object in place) means keys and records that the editor does
 * not understand still make the round trip unchanged.
 */

export const THEME_FORMAT_VERSION = 1;
export const EXTERNAL_THEME_ID_MIN = 16;
export const EXTERNAL_THEME_ID_MAX = 254;

// This list mirrors the firmware's STYLE_NAMES[] catalog. Theme authoring
// may use friendly labels in the UI, but JSONL must contain these wire IDs.
export const FIRMWARE_STYLE_NAMES = [
    'scr', 'bg', 'bg_click', 'bg_sec', 'pretty_color', 'btn', 'pad_inner', 'pad_small',
    'arc_indic', 'arc_bg', 'arc_knob', 'bar_bg', 'bar_indic', 'calendar_date_nums',
    'calendar_header', 'calendar_daynames', 'cpicker_bg', 'cpicker_indic', 'chart_bg',
    'chart_series_bg', 'chart_series', 'cb_bg', 'cb_bullet', 'ddlist_page', 'ddlist_sel',
    'gauge_main', 'gauge_strong', 'gauge_needle', 'kb_bg', 'led', 'lmeter', 'list_bg',
    'list_btn', 'mbox_bg', 'sb', 'edge_flash', 'roller_bg', 'roller_sel', 'slider_knob',
    'slider_bg', 'spinbox_cursor', 'sw_knob', 'table_cell', 'tabview_btns', 'tabview_btns_bg',
    'tabview_indic', 'tabview_page_scrl', 'ta_cursor', 'ta_placeholder',
];

export const FIRMWARE_STYLE_ALIASES = Object.freeze({ screen: 'scr', button: 'btn' });

export function canonicalStyleName(name) {
    return FIRMWARE_STYLE_ALIASES[name] ?? name;
}

export const STATES = ['d', 'p', 'c', 'pc', 'f', 'e', 'fe', 'i', 'ic', 'fp'];
export const WIDGETS = [
    'scr', 'obj', 'cont', 'btn', 'btnm', 'kb', 'bar', 'sw', 'canvas', 'img',
    'imgbtn', 'label', 'line', 'arc', 'spinner', 'slider', 'cb', 'mbox', 'led',
    'page', 'tabview', 'tabview_page', 'tileview', 'roller', 'objmask', 'list',
    'list_btn', 'ddlist', 'chart', 'table', 'win', 'win_btn', 'ta', 'spinbox',
    'spinbox_btn', 'calendar', 'cpicker', 'lmeter', 'gauge',
];
export const PARTS = [
    'main', 'bg', 'indic', 'knob', 'bullet', 'btn', 'scrollable', 'scrollbar',
    'edge_flash', 'selected', 'list', 'tab_bg', 'tab_btn', 'series_bg', 'cursor',
    'series', 'btn_bg', 'placeholder', 'content_scrollable', 'header', 'date',
    'day_names', 'major', 'needle', 'cell1', 'cell2', 'cell3', 'cell4',
];

// This is the complete set used by the documented fixture, including the
// LVGL 7 transition, scale and line fields which are easy to miss in a short
// format summary.
export const PROPERTIES = [
    'bc', 'bgc', 'bgd', 'bco', 'bo', 'bop', 'bp', 'bs', 'bw', 'cw', 'irc', 'lc', 'ldg', 'ldw',
    'lr', 'lw', 'mb', 'ml', 'mr', 'mt', 'oc', 'oo', 'op', 'ow', 'pb', 'pi', 'pl',
    'pn', 'po', 'pr', 'prc', 'pt', 'r', 'seb', 'sec', 'sew', 'sgc', 'shc', 'shs',
    'shw', 'swid', 'sz', 'tc', 'td', 'tf', 'tls', 'tr3', 'tr4', 'tr5', 'tr6', 'tsb',
    'tsc', 'tt', 'tw', 'vc', 'vf',
];

export const COLOR_PROPERTIES = new Set([
    'bc', 'bgc', 'bco', 'lc', 'oc', 'prc', 'sc', 'sgc', 'shc', 'tc', 'tsb', 'tsc', 'vc', 'irc',
]);

const REQUIRED = {
    m: ['v', 'n', 'id', 'df', 'lv', 'cd', 'dpi', 'w', 'fl'],
    c: ['n', 'v'],
    s: ['n'],
    p: ['s', 'q', 'k', 'v'],
    a: ['w', 'p', 'x'],
};

const RECORD_KEY_ORDER = {
    m: ['r', 'v', 'n', 'id', 'df', 'lv', 'cd', 'dpi', 'w', 'fl'],
    c: ['r', 'n', 'v'],
    s: ['r', 'n'],
    p: ['r', 's', 'q', 'k', 'v'],
    a: ['r', 'w', 'p', 'x'],
};

function hasOwn(object, key) {
    return Object.prototype.hasOwnProperty.call(object, key);
}

function clone(value) {
    return JSON.parse(JSON.stringify(value));
}

function isFiniteNumber(value) {
    return typeof value === 'number' && Number.isFinite(value);
}

function isInteger(value) {
    return Number.isInteger(value);
}

function assert(condition, message, line) {
    if (!condition) {
        const suffix = line ? ` (line ${line})` : '';
        throw new Error(`${message}${suffix}`);
    }
}

function validateNumericValue(record, line) {
    const value = record.v;
    if (typeof value !== 'number') return;
    assert(Number.isFinite(value), 'Numeric values must be finite', line);

    if (record.r === 'c') {
        assert(isInteger(value) && value >= 0 && value <= 0xffff,
            'RGB565 color values must be integers from 0 through 65535', line);
    }
    if (record.r === 'p' && ['bo', 'bop', 'oo'].includes(record.k)) {
        assert(isInteger(value) && value >= 0 && value <= 255,
            `${record.k} must be an opacity from 0 through 255`, line);
    }
}

function validateRecord(record, line, context) {
    assert(record && typeof record === 'object' && !Array.isArray(record),
        'Each non-empty line must contain one JSON object', line);
    const type = record.r;
    if (!['m', 'c', 's', 'p', 'a'].includes(type)) return;
    for (const key of REQUIRED[type]) {
        assert(hasOwn(record, key), `Record r="${type}" is missing required key "${key}"`, line);
    }

    if (type === 'm') {
        assert(record.v === THEME_FORMAT_VERSION, `Unsupported theme format version ${record.v}`, line);
        assert(record.lv === 7, `Only LVGL major version 7 is supported (received ${record.lv})`, line);
        assert(typeof record.n === 'string' && record.n.trim(), 'Theme name must be a non-empty string', line);
        assert(isInteger(record.id) && record.id >= EXTERNAL_THEME_ID_MIN && record.id <= EXTERNAL_THEME_ID_MAX,
            `External theme IDs must be integers from ${EXTERNAL_THEME_ID_MIN} through ${EXTERNAL_THEME_ID_MAX}`, line);
        assert(record.df === 0 || record.df === 1 || typeof record.df === 'boolean',
            'Default flag must be 0, 1, or a boolean', line);
        for (const key of ['cd', 'dpi', 'w', 'fl']) {
            assert(isFiniteNumber(record[key]) && record[key] >= 0, `Metadata ${key} must be a non-negative number`, line);
        }
        context.metadataCount += 1;
        assert(context.metadataCount === 1, 'Theme metadata may only occur once', line);
    } else if (type === 'c') {
        assert(typeof record.n === 'string' && record.n.trim(), 'Color identifier must be a non-empty string', line);
        assert(!context.colors.has(record.n), `Duplicate color identifier "${record.n}"`, line);
        context.colors.add(record.n);
    } else if (type === 's') {
        assert(typeof record.n === 'string' && record.n.trim(), 'Style identifier must be a non-empty string', line);
        assert(FIRMWARE_STYLE_NAMES.includes(record.n), `Invalid firmware style identifier "${record.n}"`, line);
        assert(!context.styles.has(record.n), `Duplicate style identifier "${record.n}"`, line);
        context.styles.add(record.n);
    } else if (type === 'p') {
        assert(context.styles.has(record.s), `Property references unknown style "${record.s}"`, line);
        assert(STATES.includes(record.q), `Invalid state identifier "${record.q}"`, line);
        assert(PROPERTIES.includes(record.k), `Invalid style property identifier "${record.k}"`, line);
    } else if (type === 'a') {
        assert(WIDGETS.includes(record.w), `Invalid widget identifier "${record.w}"`, line);
        assert(PARTS.includes(record.p), `Invalid part identifier "${record.p}"`, line);
        assert(Array.isArray(record.x) && record.x.every((id) => typeof id === 'string'),
            'Application x must be an array of style identifiers', line);
        for (const styleId of record.x) {
            assert(context.styles.has(styleId), `Application references unknown style "${styleId}"`, line);
        }
        // Multiple apply records for the same widget/part are valid. The
        // firmware processes them in file order and later additions are
        // inserted at the LVGL style-list head; the editor's cascade resolver
        // reproduces that precedence.
    }
    validateNumericValue(record, line);
}

function makeIndexes(records) {
    const colors = new Map();
    const styles = new Map();
    const applications = new Map();
    for (const record of records) {
        if (record.type === 'c') colors.set(record.data.n, record.data);
        if (record.type === 's') styles.set(record.data.n, { declaration: record.data, properties: [] });
    }
    for (const record of records) {
        if (record.type === 'p') {
            const style = styles.get(record.data.s);
            style?.properties.push(record.data);
        }
        if (record.type === 'a') applications.set(`${record.data.w}\u0000${record.data.p}`, record.data);
    }
    return { colors, styles, applications };
}

export function parseThemeJsonl(text, options = {}) {
    if (typeof text !== 'string') throw new Error('Theme content must be text');
    const normalizeAliases = options.normalizeAliases !== false;
    const records = [];
    const context = { metadataCount: 0, colors: new Set(), styles: new Set(), applications: new Set() };
    const lines = text.split(/\r?\n/);

    lines.forEach((lineText, index) => {
        if (!lineText.trim()) return;
        let data;
        try {
            data = JSON.parse(lineText);
        } catch (error) {
            throw new Error(`Invalid JSON${` (line ${index + 1})`}: ${error.message}`);
        }
        // Accept the two legacy authoring aliases used by early Designer
        // templates, but normalize them before references are validated.
        if (normalizeAliases && data.r === 's') data.n = canonicalStyleName(data.n);
        if (normalizeAliases && data.r === 'p') data.s = canonicalStyleName(data.s);
        if (normalizeAliases && data.r === 'a' && Array.isArray(data.x)) data.x = data.x.map(canonicalStyleName);
        validateRecord(data, index + 1, context);
        records.push({
            type: ['m', 'c', 's', 'p', 'a'].includes(data.r) ? data.r : 'unknown',
            data,
            line: index + 1,
            originalIndex: index,
        });
    });

    assert(context.metadataCount === 1, 'Theme metadata record is missing');
    return { records, indexes: makeIndexes(records), sourceText: text };
}

function orderedObject(data) {
    const keys = RECORD_KEY_ORDER[data.r] || [];
    const result = {};
    for (const key of keys) if (hasOwn(data, key)) result[key] = data[key];
    // Unknown keys are intentionally retained.  Their original insertion
    // order is stable in a parsed object and is used after canonical keys.
    for (const key of Object.keys(data)) if (!hasOwn(result, key)) result[key] = data[key];
    return result;
}

export function serializeThemeJsonl(document) {
    if (!document?.records) throw new Error('Invalid theme document');
    return `${document.records.map(({ data }) => {
        const normalized = { ...data };
        if (normalized.r === 's') normalized.n = canonicalStyleName(normalized.n);
        if (normalized.r === 'p') normalized.s = canonicalStyleName(normalized.s);
        if (normalized.r === 'a' && Array.isArray(normalized.x)) normalized.x = normalized.x.map(canonicalStyleName);
        return JSON.stringify(orderedObject(normalized));
    }).join('\n')}\n`;
}

export function validateThemeDocument(document) {
    const text = serializeThemeJsonl(document);
    parseThemeJsonl(text);
    return true;
}

export function rgb565ToHex(value) {
    const rgb565 = Number(value) & 0xffff;
    const r = Math.round(((rgb565 >> 11) & 0x1f) * 255 / 31);
    const g = Math.round(((rgb565 >> 5) & 0x3f) * 255 / 63);
    const b = Math.round((rgb565 & 0x1f) * 255 / 31);
    return `#${[r, g, b].map((channel) => channel.toString(16).padStart(2, '0')).join('')}`;
}

export function hexToRgb565(hex) {
    const match = String(hex).match(/^#?([0-9a-f]{6})$/i);
    if (!match) throw new Error(`Invalid RGB888 color "${hex}"`);
    const value = Number.parseInt(match[1], 16);
    const r = Math.round(((value >> 16) & 0xff) * 31 / 255);
    const g = Math.round(((value >> 8) & 0xff) * 63 / 255);
    const b = Math.round((value & 0xff) * 31 / 255);
    return (r << 11) | (g << 5) | b;
}

export function createNewTheme(name = 'new-theme') {
    const records = [
        { r: 'm', v: 1, n: name, id: 16, df: 0, lv: 7, cd: 16, dpi: 100, w: 240, fl: 49 },
        { r: 'c', n: 'background', v: 0 },
        { r: 'c', n: 'foreground', v: 0xffff },
        { r: 'c', n: 'primary', v: 0x07ff },
        { r: 's', n: 'scr' },
        { r: 'p', s: 'scr', q: 'd', k: 'bc', v: 'background' },
        { r: 'p', s: 'scr', q: 'd', k: 'tc', v: 'foreground' },
        { r: 's', n: 'btn' },
        { r: 'p', s: 'btn', q: 'd', k: 'bc', v: 'primary' },
        { r: 'p', s: 'btn', q: 'd', k: 'tc', v: 'foreground' },
        { r: 'a', w: 'scr', p: 'main', x: ['scr'] },
        { r: 'a', w: 'btn', p: 'main', x: ['btn'] },
    ];
    return parseThemeJsonl(records.map((record) => JSON.stringify(record)).join('\n'));
}

export function recordTypeLabel(type) {
    return ({ m: 'Metadata', c: 'Color', s: 'Style', p: 'Property', a: 'Application', unknown: 'Unknown' })[type] || type;
}

export function isColorProperty(key) {
    return COLOR_PROPERTIES.has(key);
}

export function propertyDescription(key) {
    return ({
        bc: 'background color', bgc: 'background gradient color', bgd: 'background gradient direction',
        bco: 'border color', bo: 'border opacity', bop: 'border opacity',
        bw: 'border width', r: 'radius', tc: 'text color', vc: 'value color', vf: 'value font',
        pl: 'padding left', pr: 'padding right', pt: 'padding top', pb: 'padding bottom',
        pn: 'padding inner', ml: 'margin left', mr: 'margin right', mt: 'margin top', mb: 'margin bottom',
        oc: 'outline color', ow: 'outline width', oo: 'outline opacity', op: 'outline padding',
        lc: 'line color', lw: 'line width', lr: 'line rounded', shc: 'shadow color',
        shw: 'shadow width', shs: 'shadow spread', swid: 'scale width', sz: 'size',
        tt: 'transition time', td: 'transition delay',
    })[key] || 'LVGL 7 style property';
}

export function propertyRange(key) {
    if (['bo', 'bop', 'oo'].includes(key)) return '0–255';
    if (['bw', 'lw', 'shw', 'shs', 'swid', 'sz'].includes(key)) return 'integer ≥ 0';
    if (['pl', 'pr', 'pt', 'pb', 'pn', 'ml', 'mr', 'mt', 'mb', 'tw'].includes(key)) return 'integer';
    return '';
}

export function rebuildIndexes(document) {
    document.indexes = makeIndexes(document.records);
    return document;
}

export function cloneTheme(document) {
    return parseThemeJsonl(serializeThemeJsonl({ records: document.records.map((record) => ({ data: clone(record.data) })) }));
}
